import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ConfirmModal from './ConfirmModal';
import './TrainingPlanTable.css';

const API_URL = '/api';

export const DEFAULT_PLAN_ROWS = [
  { id: 'row-0', set: '', exercise: '', video: '', reps: '', tempo: '' },
  { id: 'row-1', set: 'A1', exercise: '', video: '', reps: '', tempo: '' },
  { id: 'row-2', set: 'A2', exercise: '', video: '', reps: '', tempo: '' },
  { id: 'row-3', set: 'A3', exercise: '', video: '', reps: '', tempo: '' },
  { id: 'row-4', set: 'B1', exercise: '', video: '', reps: '', tempo: '' },
  { id: 'row-5', set: 'B2', exercise: '', video: '', reps: '', tempo: '' },
  { id: 'row-6', set: 'B3', exercise: '', video: '', reps: '', tempo: '' },
  { id: 'row-7', set: '', exercise: '', video: '', reps: '', tempo: '' }
];

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatDisplayDateEn(dateStr) {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      return `${MONTHS_EN[month - 1]} ${day}, ${year}`;
    }
  } catch {
    // fallback
  }
  return dateStr;
}

function formatCommentDate(value) {
  if (!value) return '';
  let strVal = String(value).trim();
  // If backend returned naive UTC string without 'Z' or timezone offset, treat it as UTC
  if (!strVal.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(strVal)) {
    strVal += 'Z';
  }
  const parsed = new Date(strVal);
  if (Number.isNaN(parsed.getTime())) return value;
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${day}.${month}. ${hours}:${minutes}`;
}

function TrainingPlanTable({
  clientName,
  dateStr,
  initialData,
  onSave,
  onAddComment,
  onDeletePlan,
  onBack,
  auth,
  canEditPlan,
  isTemplate = false,
  templateName: propTemplateName = '',
  onTemplateNameChange,
  onSaveTemplate
}) {
  const rawRole = (auth?.user?.role || '').toUpperCase();
  const role = rawRole === 'ADMIN' ? 'ADMIN' : (rawRole === 'TRAINER' ? 'TRAINER' : 'CLIENT');
  const allowPlanEdit = canEditPlan ?? (role === 'TRAINER');

  const [templateName, setTemplateName] = useState(() => {
    return propTemplateName || initialData?.name || '';
  });

  const [trainingNumber, setTrainingNumber] = useState(() => {
    return initialData?.trainingNumber || 1;
  });

  const [status, setStatus] = useState(() => {
    return initialData?.status || 'pending'; // 'completed' | 'skipped' | 'pending'
  });

  const [rows, setRows] = useState(() => {
    if (initialData?.rows && Array.isArray(initialData.rows) && initialData.rows.length > 0) {
      return initialData.rows;
    }
    return allowPlanEdit ? DEFAULT_PLAN_ROWS : [];
  });

  const [comments, setComments] = useState(() => {
    if (initialData?.comments && Array.isArray(initialData.comments)) {
      return initialData.comments;
    }
    const legacyComment = initialData?.userComment || initialData?.user_comment;
    if (legacyComment && String(legacyComment).trim()) {
      return [{
        id: `legacy-${Date.now()}`,
        authorName: clientName || 'Client',
        authorRole: 'CLIENT',
        text: legacyComment,
        createdAt: ''
      }];
    }
    return [];
  });

  const [newCommentText, setNewCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNameRequiredModal, setShowNameRequiredModal] = useState(false);
  const commentTextareaRef = useRef(null);
  const selectedVideoRowRef = useRef(null);
  const [exerciseModalRowIdx, setExerciseModalRowIdx] = useState(null);
  const [exerciseModalCustomName, setExerciseModalCustomName] = useState('');
  const [exerciseModalCustomUrl, setExerciseModalCustomUrl] = useState('');
  const [exerciseModalSearch, setExerciseModalSearch] = useState('');
  const [exerciseModalSelectedVideo, setExerciseModalSelectedVideo] = useState(null);
  const [exerciseVideoPreviewUrl, setExerciseVideoPreviewUrl] = useState('');
  const [exerciseVideoPreviewTitle, setExerciseVideoPreviewTitle] = useState('');
  const [exerciseVideoPreviewError, setExerciseVideoPreviewError] = useState('');
  const [exerciseLibrary, setExerciseLibrary] = useState([]);

  const normalizeText = (value) => String(value || '').trim();

  const getRowDisplayName = (row) => {
    return normalizeText(row?.customName) || normalizeText(row?.exercise) || normalizeText(row?.videoTitle) || '';
  };

  const getLibraryItemForRow = (row) => {
    if (!row) return null;
    const rowVideo = normalizeText(row.video);
    const rowVideoTitle = normalizeText(row.videoTitle);
    const rowExercise = normalizeText(row.exercise);

    if (rowVideo) {
      const byUrl = exerciseLibrary.find((item) => normalizeText(item.videoUrl) === rowVideo);
      if (byUrl) return byUrl;
    }

    if (rowVideoTitle) {
      const byTitle = exerciseLibrary.find((item) => normalizeText(item.title).toLowerCase() === rowVideoTitle.toLowerCase());
      if (byTitle) return byTitle;
    }

    if (rowExercise) {
      const exactMatch = exerciseLibrary.find((item) => normalizeText(item.title).toLowerCase() === rowExercise.toLowerCase());
      if (exactMatch) return exactMatch;
    }

    return null;
  };

  const openExerciseModal = (rowIndex) => {
    const row = rows[rowIndex] || {};
    const matchedVideo = getLibraryItemForRow(row);
    setExerciseModalRowIdx(rowIndex);
    setExerciseModalCustomName(normalizeText(row.customName) || normalizeText(row.exercise) || normalizeText(row.videoTitle));
    setExerciseModalCustomUrl(normalizeText(row.customUrl));
    setExerciseModalSearch('');
    setExerciseModalSelectedVideo(matchedVideo || (normalizeText(row.video) ? {
      title: normalizeText(row.videoTitle) || normalizeText(row.exercise),
      videoUrl: normalizeText(row.video),
      thumbnailUrl: normalizeText(row.thumbnailUrl)
    } : null));
  };

  const getExerciseVideoEmbedUrl = (videoUrl) => {
    const normalizedUrl = normalizeText(videoUrl);
    if (!normalizedUrl) {
      return {
        embedUrl: '',
        isPlayable: false,
        errorMessage: 'Video URL is missing.'
      };
    }

    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';

    const buildYouTubeEmbedUrl = (videoId) => {
      const params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        rel: '0',
        modestbranding: '1'
      });

      if (origin) {
        params.set('origin', origin);
      }

      return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
    };

    try {
      const parsedUrl = new URL(normalizedUrl);
      const host = parsedUrl.hostname.replace(/^www\./, '');

      if (host === 'youtu.be') {
        const videoId = parsedUrl.pathname.split('/').filter(Boolean)[0];
        if (videoId) {
          return {
            embedUrl: buildYouTubeEmbedUrl(videoId),
            isPlayable: true,
            errorMessage: ''
          };
        }
        return {
          embedUrl: '',
          isPlayable: false,
          errorMessage: 'This video cannot be played.'
        };
      }

      if (host.includes('youtube.com')) {
        const videoId = parsedUrl.searchParams.get('v');
        if (videoId) {
          return {
            embedUrl: buildYouTubeEmbedUrl(videoId),
            isPlayable: true,
            errorMessage: ''
          };
        }
      }
    } catch {
      // fall through to raw URL
    }

    return {
      embedUrl: '',
      isPlayable: false,
      errorMessage: 'This video cannot be played.'
    };
  };

  const getExerciseVideoPlaybackInfo = getExerciseVideoEmbedUrl;

  const openExerciseVideoPreview = (title, playbackInfo) => {
    setExerciseVideoPreviewTitle(title || 'Video preview');
    setExerciseVideoPreviewUrl(playbackInfo?.embedUrl || '');
    setExerciseVideoPreviewError(playbackInfo?.isPlayable ? '' : (playbackInfo?.errorMessage || 'This video cannot be played.'));
  };

  const getWorkoutPlaybackSource = (row) => {
    const selectedVideo = getLibraryItemForRow(row);
    const selectedVideoUrl = normalizeText(selectedVideo?.videoUrl || row?.video);
    const customUrl = normalizeText(row?.customUrl);

    if (selectedVideoUrl) {
      const playbackInfo = getExerciseVideoPlaybackInfo(selectedVideoUrl);
      return {
        title: selectedVideo?.title || normalizeText(row?.videoTitle) || normalizeText(row?.exercise) || 'Video preview',
        ...playbackInfo
      };
    }

    if (customUrl) {
      const playbackInfo = getExerciseVideoPlaybackInfo(customUrl);
      return {
        title: normalizeText(row?.customName) || 'Video preview',
        ...playbackInfo
      };
    }

    return null;
  };

  const handleExerciseModalPlaySelectedVideo = () => {
    if (!exerciseModalSelectedVideo?.videoUrl) return;
    const playbackInfo = getExerciseVideoPlaybackInfo(exerciseModalSelectedVideo.videoUrl);
    openExerciseVideoPreview(exerciseModalSelectedVideo.title || 'Video preview', playbackInfo);
  };

  const handleExerciseModalUrlChange = (value) => {
    setExerciseModalCustomUrl(value);
    if (exerciseModalRowIdx === null) return;

    updateExerciseRow(exerciseModalRowIdx, (currentRow) => ({
      ...currentRow,
      customUrl: value
    }));
  };

  const handleExerciseModalPlayCustomUrl = () => {
    const url = normalizeText(exerciseModalCustomUrl);
    if (!url) return;
    const playbackInfo = getExerciseVideoPlaybackInfo(url);
    openExerciseVideoPreview('Video preview', playbackInfo);
  };

  const handleExerciseModalClearCustomUrl = () => {
    setExerciseModalCustomUrl('');
    if (exerciseModalRowIdx === null) return;

    updateExerciseRow(exerciseModalRowIdx, (currentRow) => ({
      ...currentRow,
      customUrl: ''
    }));
    setExerciseVideoPreviewUrl('');
  };

  const closeExerciseVideoPreview = () => {
    setExerciseVideoPreviewUrl('');
    setExerciseVideoPreviewTitle('');
    setExerciseVideoPreviewError('');
  };

  const handleWorkoutNameClick = (rowIndex) => {
    if (allowPlanEdit) {
      openExerciseModal(rowIndex);
      return;
    }

    const playbackSource = getWorkoutPlaybackSource(rows[rowIndex]);
    if (!playbackSource) return;
    openExerciseVideoPreview(playbackSource.title, playbackSource);
  };

  const closeExerciseModal = () => {
    setExerciseModalRowIdx(null);
    setExerciseModalCustomName('');
    setExerciseModalCustomUrl('');
    setExerciseModalSearch('');
    setExerciseModalSelectedVideo(null);
    setExerciseVideoPreviewUrl('');
    setExerciseVideoPreviewTitle('');
    setExerciseVideoPreviewError('');
  };

  const updateExerciseRow = (rowIndex, updater) => {
    setRows((prevRows) => {
      const updated = [...prevRows];
      const currentRow = updated[rowIndex] || {};
      const nextRow = typeof updater === 'function' ? updater(currentRow) : { ...currentRow, ...updater };
      updated[rowIndex] = nextRow;
      return updated;
    });
  };

  const fetchExerciseLibrary = async () => {
    if (!auth?.token) return;
    try {
      const res = await axios.get(`${API_URL}/exercises/search`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setExerciseLibrary(res.data || []);
    } catch (err) {
      console.warn('Could not load exercise library:', err);
    }
  };

  useEffect(() => {
    if (auth?.token) {
      fetchExerciseLibrary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  // Keep initial snapshot to avoid unnecessary auto-save triggers on initial render
  const initialSnapshotRef = useRef(isTemplate ? JSON.stringify({
    templateName: propTemplateName || initialData?.name || '',
    rows: initialData?.rows || []
  }) : JSON.stringify({
    trainingNumber: initialData?.trainingNumber || 1,
    rows: initialData?.rows || []
  }));

  // Sync internal state when initialData updates (e.g. from async backend fetch)
  useEffect(() => {
    if (initialData) {
      if (isTemplate) {
        if (initialData.name) {
          setTemplateName(initialData.name);
        }
        let templateRows = DEFAULT_PLAN_ROWS;
        if (initialData.rows && Array.isArray(initialData.rows) && initialData.rows.length > 0) {
          templateRows = initialData.rows;
        } else if (initialData.content) {
          if (Array.isArray(initialData.content) && initialData.content.length > 0) {
            templateRows = initialData.content;
          } else if (initialData.content.rows && Array.isArray(initialData.content.rows) && initialData.content.rows.length > 0) {
            templateRows = initialData.content.rows;
          }
        }
        setRows(templateRows);
        initialSnapshotRef.current = JSON.stringify({
          templateName: initialData.name || '',
          rows: templateRows
        });
        return;
      }

      if (initialData.trainingNumber !== undefined) {
        setTrainingNumber(initialData.trainingNumber || 1);
      }
      if (initialData.status !== undefined) {
        setStatus(initialData.status || 'pending');
      }
      if (initialData.rows && Array.isArray(initialData.rows) && initialData.rows.length > 0) {
        setRows(initialData.rows);
      } else if (!initialData.rows || initialData.rows.length === 0) {
        setRows(allowPlanEdit ? DEFAULT_PLAN_ROWS : []);
      }
      if (initialData.comments && Array.isArray(initialData.comments)) {
        // Deduplicate comments by id just in case
        const seen = new Set();
        const uniqueComments = initialData.comments.filter(c => {
          if (!c.id) return true;
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        });
        setComments(uniqueComments);
      } else {
        const legacyComment = initialData?.userComment || initialData?.user_comment;
        if (legacyComment && String(legacyComment).trim()) {
          setComments([{
            id: `legacy-${Date.now()}`,
            authorName: clientName || 'Client',
            authorRole: 'CLIENT',
            text: legacyComment,
            createdAt: ''
          }]);
        } else {
          setComments([]);
        }
      }
      initialSnapshotRef.current = JSON.stringify({
        trainingNumber: initialData.trainingNumber || 1,
        rows: initialData.rows || []
      });
    }
  }, [initialData, clientName, isTemplate, allowPlanEdit]);

  // Real-time SSE listener for instant new comments on the active workout
  useEffect(() => {
    const handleCommentAdded = (e) => {
      const data = e.detail;
      if (!data?.comment) return;

      const eventWorkoutId = data.workout_id;
      const eventDate = data.workout_date;

      const matchesId = initialData?.id && eventWorkoutId && Number(initialData.id) === Number(eventWorkoutId);
      const matchesDate = dateStr && eventDate && String(dateStr) === String(eventDate);

      if (matchesId || matchesDate) {
        setComments((prev) => {
          if (prev.some((c) => c.id === data.comment.id)) {
            return prev;
          }
          return [...prev, data.comment];
        });
      }
    };

    window.addEventListener('tp:comment_added', handleCommentAdded);
    return () => {
      window.removeEventListener('tp:comment_added', handleCommentAdded);
    };
  }, [initialData?.id, dateStr]);

  // Auto-save workout plan for trainer when rows or training number change
  useEffect(() => {
    if (!rows || rows.length === 0) {
      return;
    }

    if (!allowPlanEdit) {
      return;
    }

    const currentSnapshot = JSON.stringify(
      isTemplate
        ? {
            templateName: normalizeText(templateName),
            rows
          }
        : {
            trainingNumber,
            status,
            rows
          }
    );

    if (currentSnapshot === initialSnapshotRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (isTemplate) {
        const trimmedTemplateName = normalizeText(templateName);
        if (!trimmedTemplateName || !onSaveTemplate) return;
        Promise.resolve(onSaveTemplate({ name: trimmedTemplateName, rows }, false))
          .finally(() => {
            initialSnapshotRef.current = currentSnapshot;
          });
        return;
      }

      if (onSave) {
        Promise.resolve(onSave(dateStr, {
          trainingNumber,
          status,
          rows
        })).finally(() => {
          initialSnapshotRef.current = currentSnapshot;
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isTemplate, templateName, onSaveTemplate, trainingNumber, rows, dateStr, onSave, allowPlanEdit, status, initialSnapshotRef]);

  const handleToggleStatus = () => {
    const nextStatus = (status === 'completed' || status === 'DONE') ? 'pending' : 'completed';
    setStatus(nextStatus);
    if (onSave) {
      onSave(dateStr, {
        trainingNumber,
        status: nextStatus,
        rows
      });
    }
  };

  const handleSelectTrainingNumber = (num) => {
    setTrainingNumber(num);
    if (onSave) {
      onSave(dateStr, {
        trainingNumber: num,
        status,
        rows
      });
    }
  };

  const handleCellChange = (rowIndex, field, value) => {
    if (!allowPlanEdit) return;
    setRows(prevRows => {
      const updated = [...prevRows];
      const targetRow = {
        ...updated[rowIndex],
        [field]: value
      };
      // When trainer enters or changes exercise name, auto-link video if available in library
      if (field === 'exercise' && value && String(value).trim()) {
        const cleanVal = String(value).trim().toLowerCase();
        const matched = exerciseLibrary.find(e => e.title && e.title.trim().toLowerCase() === cleanVal);
        if (matched && matched.videoUrl) {
          targetRow.video = matched.videoUrl.trim();
        }
      }
      updated[rowIndex] = targetRow;
      return updated;
    });
  };

  const handleAddRow = () => {
    if (!allowPlanEdit) return;
    setRows(prevRows => [
      ...prevRows,
      {
        id: `row-${Date.now()}`,
        set: '',
        exercise: '',
        video: '',
        reps: '',
        tempo: ''
      }
    ]);
  };

  const handleDeleteRow = (rowIndex) => {
    if (!allowPlanEdit) return;
    setRows(prevRows => prevRows.filter((_, idx) => idx !== rowIndex));
  };

  const handleDeleteEntireTraining = () => {
    if (!allowPlanEdit) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteEntireTraining = () => {
    setShowDeleteConfirm(false);
    setRows([]);
    if (onDeletePlan) {
      onDeletePlan(isTemplate ? (initialData?.id || '') : dateStr);
    }
  };

  const handleCancelDeleteEntireTraining = () => {
    setShowDeleteConfirm(false);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    const trimmed = newCommentText.trim();
    if (!trimmed) return;

    const currentUserName = auth?.user?.name || (role === 'TRAINER' ? 'Trainer' : (clientName || 'Client'));
    const isTrainer = role === 'TRAINER';
    const authorRoleLabel = isTrainer ? 'TRAINER' : 'CLIENT';

    try {
      setCommentSaving(true);
      if (onAddComment) {
        const savedComment = await onAddComment(trimmed);
        if (savedComment) {
          setComments(prev => {
            if (prev.some(c => c.id === savedComment.id)) {
              return prev;
            }
            return [...prev, savedComment];
          });
          setNewCommentText('');
          if (commentTextareaRef.current) {
            commentTextareaRef.current.style.height = 'auto';
          }
          return;
        }
      }

      const localComment = {
        id: `comm-${Date.now()}`,
        authorName: currentUserName,
        authorRole: authorRoleLabel,
        text: trimmed,
        createdAt: new Date().toISOString()
      };
      setComments(prev => [...prev, localComment]);
      setNewCommentText('');
      if (commentTextareaRef.current) {
        commentTextareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error posting comment');
    } finally {
      setCommentSaving(false);
    }
  };

  const exerciseModalQuery = normalizeText(exerciseModalSearch).toLowerCase();
  const exerciseModalResults = (exerciseLibrary || [])
    .filter((item) => {
      if (!exerciseModalQuery) return true;
      return normalizeText(item.title).toLowerCase().includes(exerciseModalQuery);
    })
    .slice(0, 5);

  const handleExerciseModalNameChange = (value) => {
    setExerciseModalCustomName(value);
    if (exerciseModalRowIdx === null) return;

    updateExerciseRow(exerciseModalRowIdx, (currentRow) => {
      const nextCustomName = value;
      const nextVideoTitle = normalizeText(currentRow.videoTitle) || normalizeText(exerciseModalSelectedVideo?.title);
      const nextDisplayName = normalizeText(nextCustomName) || nextVideoTitle || '';
      return {
        ...currentRow,
        customName: nextCustomName,
        exercise: nextDisplayName,
        videoTitle: nextVideoTitle
      };
    });
  };

  const handleExerciseModalSelectVideo = (videoItem) => {
    if (!videoItem) return;
    setExerciseModalSelectedVideo(videoItem);
    if (exerciseModalRowIdx === null) return;

    updateExerciseRow(exerciseModalRowIdx, (currentRow) => {
      const nextCustomName = normalizeText(currentRow.customName);
      const nextVideoTitle = normalizeText(videoItem.title);
      const nextDisplayName = nextCustomName || nextVideoTitle || '';
      return {
        ...currentRow,
        exercise: nextDisplayName,
        video: normalizeText(videoItem.videoUrl),
        videoTitle: nextVideoTitle
      };
    });

    window.requestAnimationFrame(() => {
      selectedVideoRowRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    });
  };

  const handleExerciseModalClearVideo = () => {
    setExerciseModalSelectedVideo(null);
    if (exerciseModalRowIdx === null) return;

    updateExerciseRow(exerciseModalRowIdx, (currentRow) => {
      const nextCustomName = normalizeText(currentRow.customName);
      return {
        ...currentRow,
        exercise: nextCustomName || '',
        video: '',
        videoTitle: ''
      };
    });
  };

  const isCompleted = status === 'completed' || status === 'DONE';

  return (
    <div className="training-table-page">
      <div className="training-table-top-bar">
        <div className="training-table-top-left">
          {onBack && (
            <button
              type="button"
              className="btn-cal-arrow"
              onClick={onBack}
              title={isTemplate ? "Back to Templates" : "Back to Calendar"}
              aria-label={isTemplate ? "Back to Templates" : "Back to Calendar"}
            >
              &lt;
            </button>
          )}

          {isTemplate ? (
            <input
              type="text"
              className="template-name-input"
              placeholder="Custom template name..."
              value={templateName}
              onChange={(e) => {
                const val = e.target.value;
                setTemplateName(val);
                if (onTemplateNameChange) onTemplateNameChange(val);
              }}
            />
          ) : (
            <span className="training-date-label">{formatDisplayDateEn(dateStr)}</span>
          )}
        </div>

        {!isTemplate && allowPlanEdit && (
          <div className="training-type-selector">
            <label>Tag:</label>
            <button
              type="button"
              className={`training-type-btn ${trainingNumber === 1 ? 'active' : ''}`}
              onClick={() => handleSelectTrainingNumber(1)}
            >
              1
            </button>
            <button
              type="button"
              className={`training-type-btn ${trainingNumber === 2 ? 'active' : ''}`}
              onClick={() => handleSelectTrainingNumber(2)}
            >
              2
            </button>
            <button
              type="button"
              className={`training-type-btn ${trainingNumber === 3 ? 'active' : ''}`}
              onClick={() => handleSelectTrainingNumber(3)}
            >
              3
            </button>
          </div>
        )}

        {isTemplate && onSaveTemplate && (
          <div className="template-top-actions">
            <button
              type="button"
              className="button button-primary btn-sm"
              onClick={() => {
                if (!templateName.trim()) {
                  setShowNameRequiredModal(true);
                  return;
                }
                onSaveTemplate({ name: templateName, rows }, true);
              }}
            >
             Save
            </button>
          </div>
        )}
      </div>

      <div className="training-spreadsheet-wrapper">
        <table className="training-spreadsheet">
          <thead>
            {/* Main header: 4 columns for clients, 5 columns when editing */}
            <tr>
              <th className="col-set">SET</th>
              <th className="col-exercise">EXERCISE</th>
              <th className="col-reps">REPS</th>
              <th className="col-tempo">TEMPO</th>
              {allowPlanEdit && <th className="col-actions"></th>}
            </tr>
          </thead>

          <tbody>
            {(() => {
              const visibleRows = rows.filter((row) => {
                if (allowPlanEdit) return true;
                const hasEx = row.exercise && String(row.exercise).trim().length > 0;
                const hasReps = row.reps && String(row.reps).trim().length > 0;
                const hasTempo = row.tempo && String(row.tempo).trim().length > 0;
                return hasEx || hasReps || hasTempo;
              });

              if (!allowPlanEdit && visibleRows.length === 0) {
                return (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '36px 16px', color: '#64748b', fontSize: '0.92rem' }}>
                      No exercises scheduled for this workout.
                    </td>
                  </tr>
                );
              }

              return rows.map((row, rowIdx) => {
                const hasEx = row.exercise && String(row.exercise).trim().length > 0;
                const hasReps = row.reps && String(row.reps).trim().length > 0;
                const hasTempo = row.tempo && String(row.tempo).trim().length > 0;
                const workoutPlaybackSource = !allowPlanEdit ? getWorkoutPlaybackSource(row) : null;

                if (!allowPlanEdit && !hasEx && !hasReps && !hasTempo) {
                  return null;
                }

                const maxLen = Math.max(3, (row.reps || '').length, (row.tempo || '').length);

                return (
                  <tr key={row.id || `row-${rowIdx}`} className="training-row-card">
                    {/* 1. SET */}
                    <td className="td-set">
                      <input
                        type="text"
                        className="cell-input set-input"
                        value={row.set || ''}
                        onChange={(e) => handleCellChange(rowIdx, 'set', e.target.value)}
                        readOnly={!allowPlanEdit}
                      />
                    </td>

                    {/* 2. EXERCISE (Merged with Video & Actions) */}
                    <td className="td-exercise">
                      <div className="exercise-cell-container">
                        <button
                          type="button"
                          className={`cell-input exercise-input exercise-display-btn ${getRowDisplayName(row) ? 'has-value' : 'is-empty'} ${!allowPlanEdit && workoutPlaybackSource ? 'has-link-source' : ''}`}
                          onClick={() => handleWorkoutNameClick(rowIdx)}
                          disabled={!allowPlanEdit && !workoutPlaybackSource}
                          title={allowPlanEdit ? 'Open workout picker' : (workoutPlaybackSource ? 'Play workout video' : 'Workout name')}
                        >
                          <span className={getRowDisplayName(row) ? 'exercise-text-name' : 'exercise-text-placeholder'}>
                            {getRowDisplayName(row) || 'Workout name'}
                          </span>
                        </button>

                        {allowPlanEdit && (
                          <div className="exercise-actions-wrapper">
                            <button
                              type="button"
                              className="btn-row-remove btn-row-remove-mobile"
                              onClick={() => handleDeleteRow(rowIdx)}
                              title="Remove row"
                              aria-label="Remove row"
                            >
                              &times;
                            </button>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* 3. REPS */}
                    <td className="td-reps">
                      <span className="mobile-cell-label">REPS:</span>
                      {!allowPlanEdit ? (
                        <span className="cell-value-text reps-text">{row.reps || '-'}</span>
                      ) : (
                        <input
                          type="text"
                          className="cell-input reps-input"
                          value={row.reps || ''}
                          onChange={(e) => handleCellChange(rowIdx, 'reps', e.target.value)}
                          placeholder=""
                          size={maxLen}
                        />
                      )}
                    </td>

                    {/* 4. TEMPO */}
                    <td className="td-tempo">
                      <span className="mobile-cell-label">TEMPO:</span>
                      {!allowPlanEdit ? (
                        <span className="cell-value-text tempo-text">{row.tempo || '-'}</span>
                      ) : (
                        <input
                          type="text"
                          className="cell-input tempo-input"
                          value={row.tempo || ''}
                          onChange={(e) => handleCellChange(rowIdx, 'tempo', e.target.value)}
                          placeholder=""
                          size={maxLen}
                        />
                      )}
                    </td>

                    {/* 5. ACTIONS (Desktop remove row button) */}
                    {allowPlanEdit && (
                      <td className="td-actions">
                        <button
                          type="button"
                          className="btn-row-remove btn-row-remove-desktop"
                          onClick={() => handleDeleteRow(rowIdx)}
                          title="Remove row"
                          aria-label="Remove row"
                        >
                          &times;
                        </button>
                      </td>
                    )}
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      {allowPlanEdit && (
        <div className="training-table-footer-controls">
          <button
            type="button"
            className="button button-secondary btn-sm"
            onClick={handleAddRow}
          >
            Add Row
          </button>
          <button
            type="button"
            onClick={handleDeleteEntireTraining}
            className="button button-danger btn-sm"
          >
            Delete
          </button>
        </div>
      )}

      {/* Workout completion status section for client */}
      {!isTemplate && role === 'CLIENT' && (
        <div className="workout-completion-section">
          {!isCompleted ? (
            <button
              type="button"
              className="btn-workout-status btn-mark-completed"
              onClick={handleToggleStatus}
            >
              Mark workout as completed
            </button>
          ) : (
            <div className="workout-completed-wrapper">
              <button
                type="button"
                className="btn-workout-status btn-is-completed"
                disabled
              >
                Workout completed!
              </button>
              <button
                type="button"
                className="btn-undo-completion"
                onClick={handleToggleStatus}
              >
                Click to undo
              </button>
            </div>
          )}
        </div>
      )}

      {/* Workout conversation & comments */}
      {!isTemplate && (
        <div className="training-comments-section">
          <div className="comments-section-header">
            <span className="comments-section-title">Comments</span>
            {comments.length > 0 && (
              <span className="comments-count-badge">{comments.length}</span>
            )}
          </div>

          {comments.length > 0 && (
            <div className="comments-chat-thread">
              {comments.map((comm) => {
                const roleUpper = (comm.authorRole || comm.author_role || '').toUpperCase();
                const isTrainer = roleUpper === 'TRAINER';
                return (
                  <div
                    key={comm.id}
                    className={`comment-row ${isTrainer ? 'comment-trainer' : 'comment-client'}`}
                  >
                    <div className="comment-head">
                      <span className="comment-author">
                        {comm.authorName || comm.author_name || (isTrainer ? 'Trainer' : 'Client')}
                      </span>
                      <span className="comment-date">
                        {formatCommentDate(comm.createdAt || comm.created_at)}
                      </span>
                    </div>
                    <div className="comment-body">{comm.text}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Minimalist comment input form */}
          <form onSubmit={handleAddComment} className="comment-input-form">
            <textarea
              ref={commentTextareaRef}
              className="comment-textarea"
              rows={1}
              maxLength={2000}
              value={newCommentText}
              onChange={(e) => {
                setNewCommentText(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              placeholder={role === 'TRAINER' ? 'Leave instructions or reply...' : 'Leave your feedback...'}
            />
            <button
              type="submit"
              className="btn-send-comment"
              disabled={!newCommentText.trim() || commentSaving}
            >
              {commentSaving ? '...' : 'Send'}
            </button>
          </form>
        </div>
      )}

      {/* Workout Picker Modal */}
      {exerciseModalRowIdx !== null && rows[exerciseModalRowIdx] && (
        <div className="video-link-modal-overlay">
          <div className="video-link-modal exercise-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="video-link-modal-header">
              <div className="video-link-modal-header-title">
                {(() => {
                  const workoutSetLabel = normalizeText(rows[exerciseModalRowIdx]?.set);
                  return /^[AB]\d+$/.test(workoutSetLabel) ? `${workoutSetLabel} Workout` : 'Workout';
                })()}
              </div>

              <button
                type="button"
                className="video-link-modal-close"
                onClick={closeExerciseModal}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="video-link-modal-body exercise-picker-modal-body">
              <div className="video-link-fields">
                <div className="video-link-field">
                  <input
                    type="text"
                    className="video-link-input"
                    value={exerciseModalCustomName}
                    onChange={(e) => handleExerciseModalNameChange(e.target.value)}
                    placeholder="Enter custom name"
                  />
                </div>

                <div className="video-link-field">
                  <div className="custom-url-input-row">
                    <input
                      type="url"
                      className="video-link-input custom-url-input"
                      value={exerciseModalCustomUrl}
                      onChange={(e) => handleExerciseModalUrlChange(e.target.value)}
                      placeholder="Paste custom URL"
                    />

                    {normalizeText(exerciseModalCustomUrl) && (
                      <div className="exercise-selected-video-actions custom-url-actions">
                        <button
                          type="button"
                          className="video-link-modal-play exercise-selected-video-play"
                          onClick={handleExerciseModalPlayCustomUrl}
                          aria-label="Play custom URL"
                          title="Play custom URL"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M8 5v14l11-7z"></path>
                          </svg>
                        </button>

                        <button
                          type="button"
                          className="exercise-selected-video-clear"
                          onClick={handleExerciseModalClearCustomUrl}
                          aria-label="Clear custom URL"
                          title="Clear custom URL"
                        >
                          &times;
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="video-link-field">
                  <input
                    type="text"
                    className="video-link-search-input"
                    value={exerciseModalSearch}
                    onChange={(e) => setExerciseModalSearch(e.target.value)}
                    placeholder="Search videos..."
                  />
                </div>

                {exerciseModalSelectedVideo && (
                  <div className="selected-video-row" ref={selectedVideoRowRef}>
                    <div className="exercise-selected-video">
                      {exerciseModalSelectedVideo.thumbnailUrl ? (
                        <img
                          src={exerciseModalSelectedVideo.thumbnailUrl}
                          alt={exerciseModalSelectedVideo.title || 'Selected video'}
                          className="exercise-selected-video-thumb"
                        />
                      ) : (
                        <div className="exercise-selected-video-thumb exercise-selected-video-thumb-placeholder">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 8l6 4-6 4V8z"></path>
                            <rect x="3" y="5" width="18" height="14" rx="3"></rect>
                          </svg>
                        </div>
                      )}

                      <div className="exercise-selected-video-meta">
                        <span className="exercise-selected-video-title">
                          {exerciseModalSelectedVideo.title || 'Selected video'}
                        </span>
                      </div>
                    </div>

                    <div className="exercise-selected-video-actions">
                      <button
                        type="button"
                        className="video-link-modal-play exercise-selected-video-play"
                        onClick={handleExerciseModalPlaySelectedVideo}
                        aria-label="Play selected video"
                        title="Play selected video"
                        disabled={!exerciseModalSelectedVideo?.videoUrl}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z"></path>
                        </svg>
                      </button>

                      <button
                        type="button"
                        className="exercise-selected-video-clear"
                        onClick={handleExerciseModalClearVideo}
                        aria-label="Clear selected video"
                        title="Clear selected video"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                )}

                <div className="exercise-picker-results">
                  {exerciseModalResults.length === 0 ? (
                    <div className="copy-modal-empty">
                      No videos found.
                    </div>
                  ) : (
                    exerciseModalResults.map((item) => {
                      const isSelected = exerciseModalSelectedVideo && normalizeText(exerciseModalSelectedVideo.videoUrl) === normalizeText(item.videoUrl);
                      return (
                        <button
                          key={item.id || item.videoUrl || item.title}
                          type="button"
                          className={`exercise-picker-item ${isSelected ? 'is-selected' : ''}`}
                          onClick={() => handleExerciseModalSelectVideo(item)}
                        >
                          {item.thumbnailUrl ? (
                            <img
                              src={item.thumbnailUrl}
                              alt={item.title || 'Video thumbnail'}
                              className="exercise-picker-thumb"
                            />
                          ) : (
                            <div className="exercise-picker-thumb exercise-picker-thumb-placeholder">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10 8l6 4-6 4V8z"></path>
                                <rect x="3" y="5" width="18" height="14" rx="3"></rect>
                              </svg>
                            </div>
                          )}

                          <span className="exercise-picker-title">
                            {item.title}
                          </span>

                          <span className="exercise-picker-tag">
                            Select
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(exerciseVideoPreviewUrl || exerciseVideoPreviewError) && (
        <div className="mini-player-overlay" onClick={closeExerciseVideoPreview}>
          <div className="mini-player-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mini-player-header">
              <div className="mini-player-header-left">
                <div className="mini-player-tip">
                  {exerciseVideoPreviewTitle || exerciseModalSelectedVideo?.title || 'Video preview'}
                </div>
              </div>

              <button
                type="button"
                className="mini-player-close-btn"
                onClick={closeExerciseVideoPreview}
                aria-label="Close preview"
              >
                &times;
              </button>
            </div>

            <div className={`mini-player-body ${exerciseVideoPreviewError ? 'mini-player-body-error' : ''}`}>
              {exerciseVideoPreviewError ? (
                <div className="mini-player-error-state" role="alert">
                  <div className="mini-player-error-text">{exerciseVideoPreviewError}</div>
                </div>
              ) : (
                <iframe
                  className="mini-player-iframe"
                  src={exerciseVideoPreviewUrl}
                  title={exerciseVideoPreviewTitle || exerciseModalSelectedVideo?.title || 'Video preview'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="origin"
                  allowFullScreen
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete workout / template confirmation modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        message={isTemplate
          ? `Are you sure you want to delete template ${templateName || 'Untitled Template'}?`
          : 'Are you sure you want to delete this workout?'
        }
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDeleteEntireTraining}
        onCancel={handleCancelDeleteEntireTraining}
      />

      {/* Template name required modal */}
      <ConfirmModal
        isOpen={showNameRequiredModal}
        message="Please enter a template name before saving."
        confirmText="OK"
        confirmVariant="primary"
        showCancel={false}
        onConfirm={() => setShowNameRequiredModal(false)}
      />
    </div>
  );
}

export default TrainingPlanTable;
